import { useState } from 'react';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import { CheckCircle, Clock, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { useEnrollmentDetail, PRACTICAL_TYPES, useCompleteLesson } from '@/api/instructor-lessons';

// Progreso del empleado + secciones con lecciones prácticas para completar.
// Extraído de complete-lessons.tsx por ENG-LOC-01.
export function EnrollmentLessons({ enrollmentId }: { enrollmentId: number }) {
  const { data, isLoading, error } = useEnrollmentDetail(enrollmentId);
  const completeMutation = useCompleteLesson(enrollmentId);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="No se pudo cargar el progreso del empleado." />;
  if (!data) return <EmptyState title="Sin datos" description="No hay datos disponibles." />;

  // Filter sections that have practical lessons
  const practicalSections = data.sections
    .map((s) => ({
      ...s,
      practicalLessons: s.lessons.filter((l) => PRACTICAL_TYPES.includes(l.type)),
    }))
    .filter((s) => s.practicalLessons.length > 0);

  if (practicalSections.length === 0) {
    return (
      <EmptyState
        title="Sin lecciones presenciales"
        description="Este curso no tiene lecciones de tipo práctica o presencial."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress summary */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-card-foreground">
                {data.enrollment.course.title}
              </p>
              <p className="text-xs text-muted-foreground">
                Progreso general: {data.overall_percent}%
              </p>
            </div>
          </div>
          <span
            className={cn(
              'rounded-full px-2.5 py-0.5 text-xs font-medium',
              data.enrollment.status === 'completed'
                ? 'bg-green-100 text-green-700'
                : data.enrollment.status === 'active'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-amber-100 text-amber-700',
            )}
          >
            {data.enrollment.status === 'active'
              ? 'Activo'
              : data.enrollment.status === 'completed'
                ? 'Completado'
                : data.enrollment.status}
          </span>
        </div>
      </div>

      {/* Sections with practical lessons */}
      {practicalSections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          onComplete={(lessonId) => completeMutation.mutate(lessonId)}
          isCompleting={completeMutation.isPending}
        />
      ))}
    </div>
  );
}

function SectionBlock({
  section,
  onComplete,
  isCompleting,
}: {
  section: {
    id: number;
    title: string;
    practicalLessons: Array<{
      id: number;
      title: string;
      type: string;
      is_completed: boolean;
      completed_at: string | null;
      evaluated_by_name: string | null;
    }>;
  };
  onComplete: (lessonId: number) => void;
  isCompleting: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const completed = section.practicalLessons.filter((l) => l.is_completed).length;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-muted/30 transition-colors"
      >
        <div>
          <h3 className="text-sm font-semibold text-card-foreground">{section.title}</h3>
          <p className="text-xs text-muted-foreground">
            {completed} de {section.practicalLessons.length} completada
            {section.practicalLessons.length !== 1 && 's'}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="divide-y divide-border border-t border-border">
          {section.practicalLessons.map((lesson) => (
            <div key={lesson.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-3">
                {lesson.is_completed ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <Clock className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <p className="text-sm text-card-foreground">{lesson.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {lesson.type === 'practical_record' ? 'Registro' : 'Observacion'}
                    {lesson.is_completed && lesson.evaluated_by_name && (
                      <> — Evaluado por {lesson.evaluated_by_name}</>
                    )}
                    {lesson.is_completed && lesson.completed_at && (
                      <> el {new Date(lesson.completed_at).toLocaleDateString('es')}</>
                    )}
                  </p>
                </div>
              </div>

              {!lesson.is_completed && (
                <button
                  onClick={() => onComplete(lesson.id)}
                  disabled={isCompleting}
                  className={cn(
                    'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                    'bg-primary text-primary-foreground hover:bg-primary/90',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                  )}
                >
                  {isCompleting ? 'Completando...' : 'Completar'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
